import React, { useState, useEffect } from 'react';
import Modal from './Modal';

// ── Design tokens ──────────────────────────────────────────────────────────
// Palette: slate-900 base · warm-amber accent · stone-100 surface · red-400 danger
// Type: system sans, tight tracking on labels, relaxed on body
// Signature: left-border accent strips on recipe cards (amber rule = ingredient, violet = component)

const styles = {
  // Layout
  page: {
    fontFamily: "'Inter', system-ui, sans-serif",
    color: '#1c1917',
    minHeight: '100vh',
  },

  // Header
  heading: {
    fontSize: '1.5rem',
    fontWeight: '700',
    letterSpacing: '-0.025em',
    color: '#1c1917',
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  headingAccent: {
    display: 'inline-block',
    width: '6px',
    height: '1.5rem',
    background: 'linear-gradient(to bottom, #f59e0b, #d97706)',
    borderRadius: '3px',
    flexShrink: 0,
  },

  // Grid
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '1rem',
  },

  // Dish list panel
  panel: {
    background: '#fafaf9',
    border: '1px solid #e7e5e4',
    borderRadius: '12px',
    padding: '1.25rem',
    maxHeight: '420px',
    overflowY: 'auto',
  },
  panelTitle: {
    fontSize: '0.7rem',
    fontWeight: '700',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#78716c',
    marginBottom: '0.75rem',
  },

  // Dish button
  dishBtn: (selected) => ({
    width: '100%',
    textAlign: 'left',
    padding: '0.625rem 0.875rem',
    borderRadius: '8px',
    border: selected ? '1.5px solid #f59e0b' : '1.5px solid transparent',
    background: selected ? '#fff8ed' : '#fff',
    cursor: 'pointer',
    transition: 'all 0.12s ease',
    boxShadow: selected ? '0 0 0 3px rgba(245,158,11,0.12)' : '0 1px 2px rgba(0,0,0,0.05)',
    marginBottom: '0.375rem',
    display: 'block',
  }),
  dishName: (selected) => ({
    fontWeight: '600',
    fontSize: '0.875rem',
    color: selected ? '#92400e' : '#1c1917',
    lineHeight: '1.3',
  }),
  dishPrice: {
    fontSize: '0.75rem',
    color: '#a8a29e',
    marginTop: '2px',
    fontVariantNumeric: 'tabular-nums',
  },

  // Recipe detail panel
  detailPanel: {
    border: '1px solid #e7e5e4',
    borderRadius: '12px',
    background: '#fff',
    overflow: 'hidden',
  },
  detailEmpty: {
    padding: '3rem 1.5rem',
    textAlign: 'center',
    color: '#a8a29e',
    fontSize: '0.9rem',
  },
  detailEmptyIcon: {
    fontSize: '2.5rem',
    marginBottom: '0.5rem',
    display: 'block',
  },

  // Detail header
  detailHeader: {
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid #f0ede9',
    background: '#fafaf9',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  dishTitle: {
    fontSize: '1.125rem',
    fontWeight: '700',
    color: '#1c1917',
    letterSpacing: '-0.015em',
  },
  dishTitlePrice: {
    fontSize: '0.8rem',
    color: '#78716c',
    marginTop: '3px',
    fontVariantNumeric: 'tabular-nums',
  },

  // Action buttons
  btnGroup: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  btn: (color) => ({
    padding: '0.4rem 0.875rem',
    fontSize: '0.8rem',
    fontWeight: '600',
    letterSpacing: '0.01em',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    transition: 'opacity 0.1s, transform 0.1s',
    color: '#fff',
    background: color,
    whiteSpace: 'nowrap',
  }),

  // Recipe content
  recipeBody: {
    padding: '1.25rem 1.5rem',
  },
  sectionLabel: {
    fontSize: '0.7rem',
    fontWeight: '700',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#a8a29e',
    marginBottom: '0.875rem',
  },
  emptyRecipe: {
    color: '#a8a29e',
    fontSize: '0.85rem',
    padding: '1rem 0',
  },

  // Recipe items
  recipeItem: (type) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    background: '#fafaf9',
    border: '1px solid #f0ede9',
    borderLeft: `3px solid ${type === 'ingredient' ? '#f59e0b' : '#8b5cf6'}`,
    marginBottom: '0.5rem',
  }),
  recipeItemLeft: {
    flex: 1,
  },
  recipeItemName: {
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#1c1917',
    lineHeight: '1.3',
  },
  recipeItemSub: {
    fontSize: '0.775rem',
    color: '#78716c',
    marginTop: '2px',
  },
  badge: (type) => ({
    display: 'inline-block',
    marginTop: '0.3rem',
    padding: '1px 7px',
    borderRadius: '999px',
    fontSize: '0.65rem',
    fontWeight: '700',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    background: type === 'ingredient' ? '#fef3c7' : '#ede9fe',
    color: type === 'ingredient' ? '#92400e' : '#5b21b6',
  }),
  removeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#d6d3d1',
    fontSize: '1.1rem',
    lineHeight: '1',
    padding: '0 0 0 0.5rem',
    transition: 'color 0.1s',
    flexShrink: 0,
    ':hover': { color: '#ef4444' },
  },

  // Loading state
  loadingDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: '#f59e0b',
    margin: '0 2px',
    animation: 'pulse 1s infinite',
  },

  // Modal internals
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.78rem',
    fontWeight: '600',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#57534e',
    marginBottom: '0.375rem',
  },
  input: {
    width: '100%',
    border: '1.5px solid #e7e5e4',
    borderRadius: '7px',
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    color: '#1c1917',
    background: '#fff',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.1s',
  },
  textarea: {
    width: '100%',
    border: '1.5px solid #e7e5e4',
    borderRadius: '7px',
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    color: '#1c1917',
    background: '#fff',
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  modalFooter: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    marginTop: '1.5rem',
    paddingTop: '1rem',
    borderTop: '1px solid #f0ede9',
  },
  btnGhost: {
    padding: '0.5rem 1.125rem',
    fontSize: '0.875rem',
    fontWeight: '600',
    borderRadius: '7px',
    border: '1.5px solid #e7e5e4',
    background: '#fff',
    color: '#57534e',
    cursor: 'pointer',
  },
  btnPrimary: (color = '#d97706') => ({
    padding: '0.5rem 1.125rem',
    fontSize: '0.875rem',
    fontWeight: '600',
    borderRadius: '7px',
    border: 'none',
    background: color,
    color: '#fff',
    cursor: 'pointer',
  }),

  // Prepare modal
  allocationRow: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '0.5rem',
    alignItems: 'center',
  },
  allocationItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '6px',
    padding: '0.35rem 0.75rem',
    marginTop: '0.375rem',
    fontSize: '0.8rem',
    color: '#166534',
  },
  prepareCard: {
    border: '1px solid #e7e5e4',
    borderRadius: '10px',
    padding: '1rem',
    background: '#fafaf9',
  },
};

// ── Component ─────────────────────────────────────────────────────────────

export default function RecipesTab() {
    const [dishes, setDishes] = useState([]);
    const [selectedDish, setSelectedDish] = useState(null);
    const [recipe, setRecipe] = useState(null);
    const [availableIngredients, setAvailableIngredients] = useState([]);
    const [availableComponents, setAvailableComponents] = useState([]);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [addType, setAddType] = useState('ingredient');
    const [showPrepareModal, setShowPrepareModal] = useState(false);
    const [batchesByIngredient, setBatchesByIngredient] = useState({});
    const [allocations, setAllocations] = useState({});
    const [prepareOrderId, setPrepareOrderId] = useState('');

    const [selectedItem, setSelectedItem] = useState({
        id: '',
        quantity: '',
        unit_id: '',
        notes: '',
    });

    useEffect(() => {
        fetchDishes();
    }, []);

    useEffect(() => {
        const handler = async (e) => {
            const orderId = e?.detail?.orderId;
            if (!orderId || !selectedDish) {
                setPrepareOrderId(orderId);
                setShowPrepareModal(true);
                return;
            }
            try {
                setLoading(true);
                const resp = await fetch(`/api/recipes/dishes/${selectedDish.id}/recipe`);
                const data = await resp.json();
                const resolved = data.data?.ingredients || {};
                const batchesMap = {};
                for (const key of Object.keys(resolved)) {
                    const ing = resolved[key];
                    const ingId = ing.ingredient_id || key;
                    const bResp = await fetch(`/api/inventory/batches?ingredient_id=${ingId}`);
                    if (bResp.ok) {
                        const bData = await bResp.json();
                        batchesMap[ingId] = bData || [];
                    } else {
                        batchesMap[ingId] = [];
                    }
                }
                setBatchesByIngredient(batchesMap);
                setAllocations({});
                setPrepareOrderId(orderId);
                setShowPrepareModal(true);
            } catch (err) {
                console.error('Erreur ouverture modal preparation', err);
                setPrepareOrderId(orderId);
                setShowPrepareModal(true);
            } finally {
                setLoading(false);
            }
        };
        window.addEventListener('openPrepareModal', handler);
        return () => window.removeEventListener('openPrepareModal', handler);
    }, [selectedDish]);

    const fetchDishes = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/inventory/dishes');
            const data = await response.json();
            setDishes(data.data || []);
        } catch (error) {
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadDishRecipe = async (dishId) => {
        setLoading(true);
        try {
            const [recipeRes, ingredientsRes, componentsRes, unitsRes] = await Promise.all([
                fetch(`/api/dishes/${dishId}/recipe/components`),
                fetch(`/api/dishes/${dishId}/recipe/available-ingredients`),
                fetch(`/api/dishes/${dishId}/recipe/available-components`),
                fetch(`/api/dishes/${dishId}/recipe/available-units`),
            ]);
            const recipeData = await recipeRes.json();
            const ingredientsData = await ingredientsRes.json();
            const componentsData = await componentsRes.json();
            const unitsData = await unitsRes.json();
            setRecipe(recipeData.data || []);
            setAvailableIngredients(ingredientsData.data || []);
            setAvailableComponents(componentsData.data || []);
            setUnits(unitsData.data || []);
        } catch (error) {
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    const selectDish = (dish) => {
        setSelectedDish(dish);
        loadDishRecipe(dish.id);
    };

    const submitAddItem = async () => {
        if (!selectedItem.id || !selectedItem.quantity || !selectedItem.unit_id) {
            alert('Veuillez remplir tous les champs');
            return;
        }
        try {
            setLoading(true);
            const endpoint = addType === 'ingredient'
                ? `/api/dishes/${selectedDish.id}/recipe/add-ingredient`
                : `/api/dishes/${selectedDish.id}/recipe/add-component`;
            const payload = {
                [addType === 'ingredient' ? 'ingredient_id' : 'component_id']: parseInt(selectedItem.id),
                quantity: parseFloat(selectedItem.quantity),
                unit_id: parseInt(selectedItem.unit_id),
                notes: selectedItem.notes,
            };
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                },
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            if (!response.ok) {
                alert(data.error || "Erreur lors de l'ajout");
                return;
            }
            alert('Élément ajouté avec succès');
            setShowAddModal(false);
            setSelectedItem({ id: '', quantity: '', unit_id: '', notes: '' });
            loadDishRecipe(selectedDish.id);
        } catch (error) {
            console.error('Erreur:', error);
            alert("Erreur lors de l'ajout");
        } finally {
            setLoading(false);
        }
    };

    const removeRecipeItem = async (dishIngredientId) => {
        if (!confirm('Supprimer cet élément de la recette ?')) return;
        try {
            setLoading(true);
            const response = await fetch(
                `/api/dishes/${selectedDish.id}/recipe/${dishIngredientId}`,
                {
                    method: 'DELETE',
                    headers: {
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    },
                }
            );
            const data = await response.json();
            if (!response.ok) {
                alert(data.error || 'Erreur lors de la suppression');
                return;
            }
            loadDishRecipe(selectedDish.id);
        } catch (error) {
            console.error('Erreur:', error);
            alert('Erreur lors de la suppression');
        } finally {
            setLoading(false);
        }
    };

    const showFullRecipe = async () => {
        try {
            setLoading(true);
            const response = await fetch(`/api/dishes/${selectedDish.id}/recipe/full`);
            const data = await response.json();
            if (!response.ok) { alert('Erreur'); return; }
            const recipeData = data.data;
            const ingredientsList = Object.values(recipeData.ingredients)
                .map(ing => `• ${ing.ingredient_name}: ${ing.quantity} ${ing.unit_abbreviation}`)
                .join('\n');
            alert(
                `Recette: ${recipeData.dish.name}\n\n${ingredientsList}\n\n` +
                `Coût total: $${recipeData.total_cost}\n` +
                `Marge: ${recipeData.profit_margin_percent}%\n` +
                `Disponible: ${recipeData.available ? '✓ OUI' : '✗ NON'}`
            );
        } catch (error) {
            console.error('Erreur:', error);
            alert('Erreur');
        } finally {
            setLoading(false);
        }
    };

    const openPrepareModal = async () => {
        try {
            setLoading(true);
            const resp = await fetch(`/api/recipes/dishes/${selectedDish.id}/recipe`);
            const data = await resp.json();
            const resolved = data.data?.ingredients || {};
            const batchesMap = {};
            for (const key of Object.keys(resolved)) {
                const ing = resolved[key];
                const ingId = ing.ingredient_id || key;
                const bResp = await fetch(`/api/inventory/batches?ingredient_id=${ingId}`);
                if (bResp.ok) {
                    const bData = await bResp.json();
                    batchesMap[ingId] = bData || [];
                } else {
                    batchesMap[ingId] = [];
                }
            }
            setBatchesByIngredient(batchesMap);
            setAllocations({});
            setShowPrepareModal(true);
        } catch (e) {
            console.error('Erreur préparation', e);
            alert('Erreur lors du chargement des lots');
        } finally {
            setLoading(false);
        }
    };

    const addAllocation = (ingredientId, batchId, quantity) => {
        if (!batchId || !quantity || quantity <= 0) return;
        setAllocations((prev) => {
            const copy = { ...prev };
            if (!copy[ingredientId]) copy[ingredientId] = [];
            copy[ingredientId].push({ batch_id: Number(batchId), quantity: Number(quantity) });
            return copy;
        });
    };

    const removeAllocation = (ingredientId, index) => {
        setAllocations((prev) => {
            const copy = { ...prev };
            if (!copy[ingredientId]) return copy;
            copy[ingredientId].splice(index, 1);
            if (copy[ingredientId].length === 0) delete copy[ingredientId];
            return copy;
        });
    };

    const submitPrepare = async () => {
        if (!prepareOrderId) {
            alert('Allocations générées (copier depuis la console)');
            console.log('allocations', allocations);
            setShowPrepareModal(false);
            return;
        }
        try {
            setLoading(true);
            const response = await fetch(`/api/recipes/orders/${prepareOrderId}/prepare`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                },
                body: JSON.stringify({ allocations }),
            });
            const data = await response.json().catch(() => null);
            if (!response.ok) {
                alert(data?.message || 'Erreur lors de la préparation');
                return;
            }
            alert('Préparation validée avec succès');
            setShowPrepareModal(false);
        } catch (e) {
            console.error(e);
            alert('Erreur lors de la préparation');
        } finally {
            setLoading(false);
        }
    };

    const ingredientCount = recipe ? recipe.filter(i => i.type === 'ingredient').length : 0;
    const componentCount = recipe ? recipe.filter(i => i.type === 'component').length : 0;

    return (
        <div style={styles.page}>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                .remove-btn:hover { color: #ef4444 !important; }
                .dish-btn:hover { background: #fff8ed !important; border-color: #fcd34d !important; }
                .action-btn:hover { opacity: 0.85; transform: translateY(-1px); }
                .input-field:focus { border-color: #f59e0b !important; box-shadow: 0 0 0 3px rgba(245,158,11,0.15); }
                @media (min-width: 900px) {
                    .recipes-grid { grid-template-columns: 280px 1fr !important; }
                }
            `}</style>

            {/* Page heading */}
            <h2 style={styles.heading}>
                <span style={styles.headingAccent} />
                Recettes des Plats
            </h2>

            <div className="recipes-grid" style={{ ...styles.grid, gridTemplateColumns: '1fr' }}>

                {/* ── Dish list ──────────────────────────────── */}
                <div style={styles.panel}>
                    <p style={styles.panelTitle}>
                        {loading && dishes.length === 0 ? 'Chargement…' : `${dishes.length} Plat${dishes.length !== 1 ? 's' : ''}`}
                    </p>

                    {!loading && dishes.length === 0 && (
                        <p style={{ color: '#a8a29e', fontSize: '0.85rem' }}>Aucun plat trouvé.</p>
                    )}

                    {dishes.map(dish => {
                        const selected = selectedDish?.id === dish.id;
                        return (
                            <button
                                key={dish.id}
                                className="dish-btn"
                                onClick={() => selectDish(dish)}
                                style={styles.dishBtn(selected)}
                            >
                                <div style={styles.dishName(selected)}>{dish.name}</div>
                                <div style={styles.dishPrice}>${dish.price}</div>
                            </button>
                        );
                    })}
                </div>

                {/* ── Recipe detail ──────────────────────────── */}
                <div style={styles.detailPanel}>
                    {!selectedDish ? (
                        <div style={styles.detailEmpty}>
                            <span style={styles.detailEmptyIcon}>🍽️</span>
                            Sélectionnez un plat pour voir sa recette
                        </div>
                    ) : (
                        <>
                            {/* Header */}
                            <div style={styles.detailHeader}>
                                <div>
                                    <div style={styles.dishTitle}>{selectedDish.name}</div>
                                    <div style={styles.dishTitlePrice}>
                                        Prix de vente · <strong>${selectedDish.price}</strong>
                                        {recipe && (
                                            <span style={{ marginLeft: '0.75rem', color: '#a8a29e' }}>
                                                {ingredientCount} ingrédient{ingredientCount !== 1 ? 's' : ''}
                                                {componentCount > 0 && ` · ${componentCount} composant${componentCount !== 1 ? 's' : ''}`}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div style={styles.btnGroup}>
                                    <button
                                        className="action-btn"
                                        onClick={showFullRecipe}
                                        style={styles.btn('#16a34a')}
                                        disabled={loading}
                                    >
                                        Voir recette
                                    </button>
                                    <button
                                        className="action-btn"
                                        onClick={() => { setAddType('ingredient'); setShowAddModal(true); }}
                                        style={styles.btn('#2563eb')}
                                        disabled={loading}
                                    >
                                        + Ingrédient
                                    </button>
                                    <button
                                        className="action-btn"
                                        onClick={() => { setAddType('component'); setShowAddModal(true); }}
                                        style={styles.btn('#7c3aed')}
                                        disabled={loading}
                                    >
                                        + Composant
                                    </button>
                                    <button
                                        className="action-btn"
                                        onClick={openPrepareModal}
                                        style={styles.btn('#d97706')}
                                        disabled={loading}
                                    >
                                        {loading ? '…' : 'Préparer'}
                                    </button>
                                </div>
                            </div>

                            {/* Recipe items */}
                            <div style={styles.recipeBody}>
                                <p style={styles.sectionLabel}>Composition de la recette</p>

                                {loading && (
                                    <p style={{ color: '#a8a29e', fontSize: '0.85rem' }}>Chargement…</p>
                                )}

                                {!loading && (!recipe || recipe.length === 0) && (
                                    <p style={styles.emptyRecipe}>
                                        Aucun ingrédient ou composant — ajoutez-en avec les boutons ci-dessus.
                                    </p>
                                )}

                                {!loading && recipe && recipe.map((item, idx) => (
                                    <div key={idx} style={styles.recipeItem(item.type)}>
                                        <div style={styles.recipeItemLeft}>
                                            <div style={styles.recipeItemName}>
                                                {item.type === 'ingredient' ? item.ingredient_name : item.component_name}
                                            </div>
                                            <div style={styles.recipeItemSub}>
                                                {item.quantity} {item.unit_abbreviation}
                                                {item.notes && <span style={{ color: '#a8a29e' }}> · {item.notes}</span>}
                                            </div>
                                            <span style={styles.badge(item.type)}>
                                                {item.type === 'ingredient' ? 'Ingrédient' : 'Composant'}
                                            </span>
                                        </div>
                                        <button
                                            className="remove-btn"
                                            onClick={() => removeRecipeItem(item.id)}
                                            style={styles.removeBtn}
                                            title="Supprimer"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* ── Modal: ajouter ingrédient / composant ─────── */}
            <Modal
                open={showAddModal}
                onClose={() => setShowAddModal(false)}
                title={addType === 'ingredient' ? 'Ajouter un ingrédient' : 'Ajouter un composant'}
            >
                <div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            {addType === 'ingredient' ? 'Ingrédient' : 'Composant'} *
                        </label>
                        <select
                            className="input-field"
                            value={selectedItem.id}
                            onChange={(e) => setSelectedItem({ ...selectedItem, id: e.target.value })}
                            style={styles.input}
                        >
                            <option value="">Sélectionner…</option>
                            {(addType === 'ingredient' ? availableIngredients : availableComponents).map(item => (
                                <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                        </select>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', ...styles.formGroup }}>
                        <div>
                            <label style={styles.label}>Quantité *</label>
                            <input
                                className="input-field"
                                type="number"
                                value={selectedItem.quantity}
                                onChange={(e) => setSelectedItem({ ...selectedItem, quantity: e.target.value })}
                                style={styles.input}
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                            />
                        </div>
                        <div>
                            <label style={styles.label}>Unité *</label>
                            <select
                                className="input-field"
                                value={selectedItem.unit_id}
                                onChange={(e) => setSelectedItem({ ...selectedItem, unit_id: e.target.value })}
                                style={styles.input}
                            >
                                <option value="">Sélectionner…</option>
                                {units.map(unit => (
                                    <option key={unit.id} value={unit.id}>
                                        {unit.name} ({unit.abbreviation})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Notes <span style={{ color: '#a8a29e', fontWeight: 400 }}>(optionnel)</span></label>
                        <textarea
                            className="input-field"
                            value={selectedItem.notes}
                            onChange={(e) => setSelectedItem({ ...selectedItem, notes: e.target.value })}
                            style={styles.textarea}
                            rows={2}
                            placeholder="Instructions spéciales…"
                        />
                    </div>

                    <div style={styles.modalFooter}>
                        <button
                            onClick={() => setShowAddModal(false)}
                            style={styles.btnGhost}
                            disabled={loading}
                        >
                            Annuler
                        </button>
                        <button
                            onClick={submitAddItem}
                            style={styles.btnPrimary('#2563eb')}
                            disabled={loading}
                        >
                            {loading ? 'Ajout…' : 'Ajouter'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Modal: préparer / choisir lots ───────────── */}
            <Modal
                open={showPrepareModal}
                onClose={() => setShowPrepareModal(false)}
                title="Préparer — Choisir les lots"
            >
                <div>
                    <div style={{ ...styles.formGroup, marginBottom: '1.25rem' }}>
                        <label style={styles.label}>Numéro de commande <span style={{ color: '#a8a29e', fontWeight: 400 }}>(optionnel)</span></label>
                        <input
                            className="input-field"
                            value={prepareOrderId}
                            onChange={(e) => setPrepareOrderId(e.target.value)}
                            style={styles.input}
                            placeholder="Ex. 123"
                        />
                    </div>

                    <div style={{ maxHeight: '340px', overflowY: 'auto', paddingRight: '4px' }}>
                        {recipe && recipe.filter(i => i.type === 'ingredient').length > 0 ? (
                            recipe.filter(i => i.type === 'ingredient').map((ing) => (
                                <div key={ing.ingredient_id || ing.id} style={{ ...styles.prepareCard, marginBottom: '0.75rem' }}>
                                    <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1c1917' }}>
                                        {ing.ingredient_name}
                                    </div>
                                    <div style={{ fontSize: '0.775rem', color: '#78716c', marginBottom: '0.625rem' }}>
                                        Quantité nécessaire · {ing.quantity} {ing.unit_abbreviation}
                                    </div>

                                    <div style={styles.allocationRow}>
                                        <select
                                            id={`batch-select-${ing.ingredient_id}`}
                                            className="input-field"
                                            style={{ ...styles.input, flex: 2, padding: '0.4rem 0.625rem' }}
                                            defaultValue=""
                                        >
                                            <option value="">Choisir un lot…</option>
                                            {(batchesByIngredient[ing.ingredient_id] || []).map(b => (
                                                <option key={b.id} value={b.id}>
                                                    {b.lot_number || '—'} · restant : {b.remaining_quantity}
                                                </option>
                                            ))}
                                        </select>
                                        <input
                                            id={`batch-qty-${ing.ingredient_id}`}
                                            className="input-field"
                                            type="number"
                                            step="0.01"
                                            placeholder="Qté"
                                            style={{ ...styles.input, flex: 1, padding: '0.4rem 0.625rem' }}
                                        />
                                        <button
                                            onClick={() => {
                                                const sel = document.getElementById(`batch-select-${ing.ingredient_id}`);
                                                const qty = document.getElementById(`batch-qty-${ing.ingredient_id}`);
                                                addAllocation(ing.ingredient_id, sel.value, parseFloat(qty.value));
                                                if (sel) sel.value = '';
                                                if (qty) qty.value = '';
                                            }}
                                            style={{ ...styles.btnPrimary('#16a34a'), padding: '0.4rem 0.75rem', fontSize: '0.8rem', whiteSpace: 'nowrap', flexShrink: 0 }}
                                        >
                                            Allouer
                                        </button>
                                    </div>

                                    {(allocations[ing.ingredient_id] || []).map((a, idx) => (
                                        <div key={idx} style={styles.allocationItem}>
                                            <span>{a.quantity} · Lot #{a.batch_id}</span>
                                            <button
                                                onClick={() => removeAllocation(ing.ingredient_id, idx)}
                                                style={{ background: 'none', border: 'none', color: '#16a34a', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', lineHeight: '1' }}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ))
                        ) : (
                            <p style={{ color: '#a8a29e', fontSize: '0.85rem' }}>
                                Aucun ingrédient résolu pour ce plat.
                            </p>
                        )}
                    </div>

                    <div style={styles.modalFooter}>
                        <button onClick={() => setShowPrepareModal(false)} style={styles.btnGhost}>
                            Annuler
                        </button>
                        <button onClick={submitPrepare} style={styles.btnPrimary('#d97706')} disabled={loading}>
                            {loading ? 'Validation…' : 'Valider la préparation'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}